import React, { useEffect, useState } from "react"
import { LoadingFlex, Stat } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import DownloadCsv from './components/DownloadCsv'
import {useIntl, FormattedMessage} from "react-intl"
import messages from "./messages"
import DateTime from 'react-datetime'

const ErrorRates = ({ customer }) => {
    const { formatMessage } = useIntl()
    const [loadingErrorRate, setLoadingErrorRate] = useState(false)
    const [customerErrorRate, setCustomerErrorRate] = useState(true)

    const now = new Date()
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())

    const [dateRange, setDateRange] = useState({ fromDate: oneYearAgo, toDate: now });


    useEffect(() => {
        setLoadingErrorRate(true)
        const params = new URLSearchParams(Object.entries({
            fromDate: Math.round(new Date(dateRange.fromDate).getTime() / 1000),
            toDate: Math.round(new Date(dateRange.toDate).getTime() / 1000),
        }))
        fetch(`/rest/v1/customer/${customer.uid}/error-rate?${params.toString()}`)
            .then((response) => response.json())
            .then((data) => {
                setCustomerErrorRate(data)
                setLoadingErrorRate(false)
            })
            .catch((err) => {
                toastr.error(err)
            })
    }, [dateRange, customer])

    const getOrdersWithIncidents = (column) => {
        return customerErrorRate?.data?.orders.length
            ? customerErrorRate?.data?.orders.filter((order) => order[column] > 0)
            : []
    }

    return (
        <div className="p-4 text-center">
            {loadingErrorRate ? (
                <LoadingFlex>
                    <div className="flex items-center space-x-1">
                        <span className="font-medium"><FormattedMessage {...messages.loading} /></span>
                        <Icon icon={["fal", "spinner-third"]} className="animate-spin" />
                    </div>
                </LoadingFlex>
            ) : (
                <div className="mx-2 text-left">
                    <div className="flex justify-between">
                        <div className="text-bold text-xl mt-auto mb-1">
                            <FormattedMessage {...messages.title} />
                        </div>
                        <div className="flex gap-2 mb-2">
                            <div>
                                <label className="text-gray-700 text-sm font-medium">From:</label>
                                <DateTime
                                    value={dateRange.fromDate}
                                    inputProps={{
                                        disabled: loadingErrorRate,
                                        className: 'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500'
                                    }}
                                    onChange={(value) => setDateRange((prev) => ({ ...prev, fromDate: value }))}
                                    closeOnTab
                                />
                            </div>
                            <div>
                                <label className="text-gray-700 text-sm font-medium">To:</label>
                                <DateTime
                                    value={dateRange.toDate}
                                    inputProps={{
                                        disabled: loadingErrorRate,
                                        className: 'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500'
                                    }}
                                    onChange={(value) => setDateRange((prev) => ({ ...prev, toDate: value }))}
                                    closeOnTab
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 flex-initial w-full text-left order-3 md:order-none">
                        <Stat
                            title={formatMessage(messages.statTitle, {
                                totalOrdersWithIncidents: customerErrorRate?.data?.totalOrdersWithExternalErrors,
                                totalOrders: customerErrorRate?.data?.totalOrders
                            })}
                            name={formatMessage(messages.externalErrorRateLabel)}
                            icon="network-wired"
                            data={{
                                current: parseFloat(customerErrorRate?.data?.externalErrorRate).toFixed(2),
                                kpi: customerErrorRate?.meta?.kpi?.externalErrorRate
                            }}
                            symbol="%"
                        >
                            <DownloadCsv orders={getOrdersWithIncidents('ExternalErrorRate')} fileName={formatMessage(messages.externalErrorRateLabel)}/>
                        </Stat>
                        <Stat
                            title={formatMessage(messages.statTitle, {
                                totalOrdersWithIncidents: customerErrorRate?.data?.totalOrdersWithInternalErrors,
                                totalOrders: customerErrorRate?.data?.totalOrders
                            })}
                            name={formatMessage(messages.internalErrorRateLabel)}
                            icon="server"
                            data={{
                                current: parseFloat(customerErrorRate?.data?.internalErrorRate).toFixed(2),
                                kpi: customerErrorRate?.meta?.kpi?.internalErrorRate
                            }}
                            symbol="%"
                        >
                            <DownloadCsv orders={getOrdersWithIncidents('InternalErrorRate')} fileName={formatMessage(messages.internalErrorRateLabel)}/>
                        </Stat>
                        <Stat
                            title={formatMessage(messages.statTitle, {
                                totalOrdersWithIncidents: customerErrorRate?.data?.totalOrdersWithInternalErrors + customerErrorRate?.data?.totalOrdersWithExternalErrors,
                                totalOrders: customerErrorRate?.data?.totalOrders
                            })}
                            name={formatMessage(messages.totalErrorRateLabel)}
                            icon="bug"
                            data={{
                                current: parseFloat(customerErrorRate?.data?.totalErrorRate).toFixed(2),
                                kpi: customerErrorRate?.meta?.kpi?.totalErrorRate
                            }}
                            symbol="%"
                        >
                            <DownloadCsv orders={[...getOrdersWithIncidents('ExternalErrorRate'), ...getOrdersWithIncidents('InternalErrorRate')]} fileName={formatMessage(messages.totalErrorRateLabel)}/>
                        </Stat>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ErrorRates
